<template>
  <label
    class="w-full min-h-[200px] flex flex-col justify-center items-center border-dashed border-2 border-[var(--p-inputtext-border-color)] cursor-pointer text-center mb-2 p-4 rounded-md gap-2"
    :class="{ 'bg-gray-400': dragOver }"
    @dragover.prevent="dragOver = true"
    @dragleave.prevent="dragOver = false"
    @drop.prevent="
      dragOver = false;
      onUpload($event);
    "
    :for="`imageFile-${id}`"
    data-test="upload-prompt"
  >
    <input
      :name="props.name"
      type="file"
      accept="image/*"
      @change="onUpload($event)"
      ref="imageFile"
      :id="`imageFile-${id}`"
      class="hidden"
      v-bind="props.fieldProps"
      data-test="file-input"
    />
    <template v-if="uploading">
      <div class="p-4 w-full">
        <ProgressBar
          :mode="uploadProgressMode"
          :value="uploadProgressMode ? null : uploadProgress"
          class="w-full h-2"
        ></ProgressBar>
      </div>
    </template>
    <template v-else-if="image">
      <img
        :src="image"
        class="preview-image object-cover rounded-lg shadow-md"
        :class="thumbnailClass"
        :data-test="`image-preview-single`"
      />
      <a
        href
        @click.prevent="removeImage()"
        class="text-sm"
        data-test="remove-image"
        ><i class="fa fa-trash mr-2"></i>Remove</a
      >
    </template>
    <template v-else-if="images.length > 0">
      <div class="flex self-start w-full gap-2 flex-wrap">
        <div
          v-for="(img, i) in images"
          :key="img"
          class="flex flex-col items-center gap-2 relative group"
        >
          <img
            :src="img"
            class="preview-image object-cover rounded-lg shadow-md"
            :class="thumbnailClass"
            :data-test="`image-preview-${i}`"
          />
          <!-- Round trash icon in upper left corner, visible only on parent hover -->
          <a
            href
            @click.prevent="removeImage(i)"
            class="text-sm absolute top-[-0.5rem] right-[-0.5rem] w-6 h-6 rounded-full bg-white flex justify-center items-center shadow-md hover:bg-red-500 hover:text-white transition-all duration-200 opacity-0 group-hover:opacity-100"
            :data-test="`remove-image-${i}`"
            ><i class="fa fa-trash"></i
          ></a>
        </div>
      </div>
      <p class="mt-3">Drop or click to upload additional files</p>
    </template>
    <template v-else>
      <UploadCloud />
      <p>Drop file here or click to upload</p>
      <p class="text-xs">
        Images should be a minimum of {{ props.minSize }}&times;{{ props.minSize }}px and look best in a 4:3 aspect
        ratio. Large images will be automatically resized. Accepted image types: JPEG, PNG.
      </p>
    </template>
  </label>
</template>

<script setup>
import { ref, defineEmits, defineProps, watch, onBeforeMount } from 'vue';
import api from '@/js/api.js';
import { handleImageUpload } from '@/helpers/imageUpload.js';
import { useToastStore } from '@/stores/ToastStore.js';
import UploadCloud from '@/assets/UploadCloud.vue';

const dragOver = ref(false);
const id = ref(Math.random().toString(36).substring(7));
const image = ref(null);
const images = ref([]);
const imageFile = ref(null);
const thumbnailClass = ref('');
const uploading = ref(false);
const uploadProgress = ref(0);
const uploadProgressMode = ref(null);

const props = defineProps({
  auto: {
    type: Boolean,
    default: false,
  },
  fieldProps: {
    type: Object,
  },
  name: {
    type: String,
    default: 'image',
  },
  minSize: {
    type: Number,
    default: 400,
  },
  maxSize: {
    type: Number,
    default: 1280,
  },
  multi: {
    type: Boolean,
    default: false,
  },
  thumbnailClass: {
    type: String,
    default: '', // See defaults below in onBeforeMount
  },
  value: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(['uploaded']);

onBeforeMount(() => {
  if (!props.thumbnailClass) {
    thumbnailClass.value = props.multi ? 'w-32 h-20' : 'w-full h-48';
  } else {
    thumbnailClass.value = props.thumbnailClass;
  }
});

watch(
  () => props.value,
  (newValue) => {
    if (props.multi) {
      if (Array.isArray(newValue.value) || newValue.value === null) {
        images.value = newValue.value ?? [];
      } else {
        throw new Error('Value must be an array for multi-image upload');
      }
    } else if (newValue.value) {
      if (typeof newValue.value === 'string') {
        image.value = newValue.value;
      } else {
        throw new Error('Value must be a string for single-image upload');
      }
    }
  },
);

const onUpload = async (e) => {
  const file = e.dataTransfer ? e.dataTransfer.files[0] : e.target.files[0];
  if (!file) return;
  image.value = null;

  try {
    const imageBase64 = await handleImageUpload(file, props.minSize, props.maxSize);
    if (props.auto) {
      uploading.value = true;
      uploadProgress.value = 0;
      uploadProgressMode.value = null;

      // Upload with Axios (api.post), displaying progress as we go
      const res = await api.post(
        'images',
        { imageBase64 },
        {
          onUploadProgress: (progressEvent) => {
            uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            if (uploadProgress.value === 100) {
              uploadProgressMode.value = 'indeterminate';
            }
          },
        },
      );

      if (res.data.code >= 400) {
        throw new Error(res.data.data);
      }
      if (props.multi) {
        images.value.push(res.data.url);
      } else {
        image.value = res.data.url;
      }
    } else {
      if (props.multi) {
        images.value.push(imageBase64);
      } else {
        image.value = imageBase64;
      }
    }

    if (props.multi) {
      emit('uploaded', images.value);
      imageFile.value.value = null;
    } else {
      emit('uploaded', image.value);
    }
  } catch (e) {
    useToastStore().add({
      severity: 'error',
      summary: 'Error',
      detail: e.response?.data ?? e.message ?? e,
      life: 3000,
    });
  } finally {
    uploading.value = false;
  }
};

const openFilePicker = () => {
  imageFile.value.click();
};

const removeImage = (i) => {
  if (props.multi) {
    images.value.splice(i, 1);
    emit('uploaded', images.value);
  } else {
    image.value = null;
    imageFile.value.value = null;
    emit('uploaded', null);
  }
};
</script>
